import { Link } from "@tanstack/react-router";
import { Alert, Button, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { LeadFilterBar } from "../components/LeadFilterBar";
import { useLeadFilters } from "../hooks/useLeadFilters";
import { buildLeadParams, useLeadExport, useLeadsList } from "../hooks/useLeads";
import { type Lead, SUSPICION_LABELS } from "../types";

const { Title } = Typography;

/**
 * ⚠️ Nomi kontrakt §9 da muzlatilgan (`routeTree.tsx` shu nom bilan
 * import qiladi) — eksport nomi O'ZGARTIRILMAYDI.
 */
export function LeadsPage() {
	const { t } = useTranslation("leads");
	const { filters, setFilters } = useLeadFilters();
	const query = useLeadsList(filters);
	const exportJob = useLeadExport();

	// ⚠️ `formId` MAJBURIY (spec §8.3) — tugma usiz o'chirilgan bo'ladi
	// va sabab tooltip'da ko'rsatiladi. Aks holda foydalanuvchi 422
	// olardi va nima uchunligini tushunmasdi.
	const eksportTayyor = Boolean(filters.formId);

	const columns: ColumnsType<Lead> = [
		{
			title: t("list.columns.date"),
			key: "occurredAt",
			render: (_, row) => dayjs(row.occurredAt).format("YYYY-MM-DD HH:mm"),
		},
		{
			title: t("list.columns.employee"),
			key: "employee",
			render: (_, row) => row.employeeName ?? row.employeeId,
		},
		{
			title: t("list.columns.form"),
			key: "form",
			render: (_, row) => t("list.formValue", { name: row.formName, version: row.formVersion }),
		},
		{ title: t("list.columns.location"), dataIndex: "resolvedLabel", key: "resolvedLabel" },
		{
			title: t("list.columns.task"),
			key: "task",
			render: (_, row) => row.taskName ?? "—",
		},
		{
			title: t("list.columns.flags"),
			key: "flags",
			render: (_, row) =>
				row.suspicionFlags.length === 0 ? (
					"—"
				) : (
					<Space size={4} wrap>
						{row.suspicionFlags.map((flag) => (
							<Tag color="orange" key={flag}>
								{t(`suspicion.${flag}`, SUSPICION_LABELS[flag] ?? flag)}
							</Tag>
						))}
					</Space>
				),
		},
		{
			title: "",
			key: "actions",
			render: (_, row) => (
				<Link to="/leads/$leadId" params={{ leadId: row.id }}>
					<Button type="link">{t("list.detailButton")}</Button>
				</Link>
			),
		},
	];

	return (
		<div>
			<Space className="mb-4 w-full justify-between">
				<Title level={3}>{t("list.title")}</Title>
				{/*
				 * ⚠️ `/leads/map` da `validateSearch` YO'Q (`routeTree.tsx`
				 * poydevor fayli, F2 unga tegmaydi). `search` filtr obyektini
				 * to'g'ridan-to'g'ri berish o'rniga JORIY qidiruv satrini
				 * SAQLAYDI — filtrlar allaqachon `useLeadFilters` orqali URL
				 * da yashaydi (spec §8.4), shuning uchun bu naqsh aynan
				 * to'g'ri: ikkinchi marta serializatsiya qilinmaydi va
				 * `LeadListQuerySchema` bilan bog'lanmagan cast talab
				 * qilinmaydi.
				 */}
				<Space>
					<Tooltip title={eksportTayyor ? "" : t("list.exportTooltip")}>
						<Button
							disabled={!eksportTayyor}
							loading={exportJob.request.isPending || exportJob.isPolling}
							onClick={() => exportJob.request.mutate(buildLeadParams(filters))}
						>
							{t("list.exportButton")}
						</Button>
					</Tooltip>
					{exportJob.status?.status === "done" ? (
						<Button type="primary" onClick={() => exportJob.download()}>
							{t("list.downloadButton", { count: exportJob.status.rowCount ?? 0 })}
						</Button>
					) : null}
					{exportJob.status?.status === "failed" ? (
						<Alert
							type="error"
							showIcon
							message={exportJob.status.error ?? t("list.exportFailed")}
						/>
					) : null}
					<Link to="/leads/map" search={true}>
						<Button>{t("list.mapButton")}</Button>
					</Link>
				</Space>
			</Space>

			<LeadFilterBar filters={filters} onChange={setFilters} />

			<DataTable<Lead> columns={columns} query={query} rowKey="id" />
		</div>
	);
}
